// src/components/personal/sections/SettingsSection/AppSettings/AppSettingsForm.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Globe, Mail, MessageSquare, CreditCard, Settings, TrendingUp } from 'lucide-react';
import Message from '@/components/ui/Message';
import { AppSettingsFormProps } from './types';
import { getAssistantInstructionsHelp } from './utils';
import { COMPONENT_STYLES } from '@/theme/components';
import { COLORS } from '@/theme/colors';
import { Card, Flex, Grid } from '@/components/ui/Layout';
import { useResponsive } from '@/hooks/useResponsive';

const AppSettingsForm: React.FC<AppSettingsFormProps> = ({
  settings,
  businessTypes,
  timeZones,
  currencies,
  saving,
  success,
  error,
  onChange,
  onSubmit,
  hasChanges
}) => {
  // Загружаем несколько пространств имен для разделения переводов
  const { t: tProfile } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const { isMobile } = useResponsive();
  
  const inputBaseClass = COMPONENT_STYLES.form.input;
  const labelClass = COMPONENT_STYLES.form.label;
  const formGroupClass = COMPONENT_STYLES.form.group;
  const checkboxClass = COMPONENT_STYLES.form.checkbox.input;
  const checkboxLabelClass = COMPONENT_STYLES.form.checkbox.label;
  
  // Пользовательский обработчик для числовых полей, которые нужно преобразовать из % в десятичное число
  const handlePercentageInput = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const numberValue = parseFloat(e.target.value) / 100;
    // Создаем новый синтетический event с строковым значением вместо числового
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: fieldName,
        value: String(numberValue) // Конвертируем число в строку
      } as EventTarget & HTMLInputElement
    };
    
    onChange(syntheticEvent);
  };
  
  return (
    <form onSubmit={onSubmit} className={COMPONENT_STYLES.form.container}>
      <Grid cols={isMobile ? 1 : 1} gap="md" mobileCols={1}>
        {/* Тип бизнеса */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <Database className="w-4 h-4 text-[#245D33]" />
            <label htmlFor="business_type" className={labelClass}>
              {tProfile('business_type')}
            </label>
          </Flex>
          <select
            id="business_type"
            name="business_type"
            value={settings.business_type}
            onChange={onChange}
            className={inputBaseClass}
          >
            {businessTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Facebook ID */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <Globe className={`w-4 h-4 text-primary`} />
            <label htmlFor="facebook_business_id" className={labelClass}>
              {tProfile('facebook_business_id')}
            </label>
          </Flex>
          <input
            type="text"
            id="facebook_business_id"
            name="facebook_business_id"
            value={settings.facebook_business_id}
            onChange={onChange}
            className={inputBaseClass}
            placeholder={tProfile('facebook_business_id_placeholder')}
          />
          <p className={`mt-1 text-xs text-[${COLORS.secondary}]`}>
            {tProfile('facebook_business_id_description')}
          </p>
        </div>

        {/* Бизнес Email с чекбоксом */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <Mail className={`w-4 h-4 text-primary`} />
            <label htmlFor="business_email" className={labelClass}>
              {tProfile('business_email')}
            </label>
          </Flex>
          <Flex align="center" gap="md">
            <input
              type="email"
              id="business_email"
              name="business_email"
              value={settings.business_email || ''}
              onChange={onChange}
              className={`flex-1 ${inputBaseClass}`}
              placeholder="business@example.com"
            />
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="business_email_for_assistant"
                name="business_email_for_assistant"
                checked={Boolean(settings.business_email_for_assistant)}
                onChange={onChange}
                className={checkboxClass}
              />
              <label htmlFor="business_email_for_assistant" className={checkboxLabelClass}>
                {tProfile('use_for_assistant')}
              </label>
            </div>
          </Flex>
        </div>

        {/* WhatsApp номер с чекбоксом */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <MessageSquare className={`w-4 h-4 text-primary`} />
            <label htmlFor="whatsapp_number" className={labelClass}>
              {tProfile('whatsapp_number')}
            </label>
          </Flex>
          <Flex align="center" gap="md">
            <input
              type="text"
              id="whatsapp_number"
              name="whatsapp_number"
              value={settings.whatsapp_number || ''}
              onChange={onChange}
              className={`flex-1 ${inputBaseClass}`}
              placeholder="+7XXXXXXXXXX"
            />
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="whatsapp_for_assistant"
                name="whatsapp_for_assistant"
                checked={Boolean(settings.whatsapp_for_assistant)}
                onChange={onChange}
                className={checkboxClass}
              />
              <label htmlFor="whatsapp_for_assistant" className={checkboxLabelClass}>
                {tProfile('use_for_assistant')}
              </label>
            </div>
          </Flex>
          <p className={`mt-1 text-xs text-[${COLORS.secondary}]`}>
            {tProfile('whatsapp_number_description')}
          </p>
        </div>

        {/* Временная зона */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <Globe className={`w-4 h-4 text-primary`} />
            <label htmlFor="business_utc" className={labelClass}>
              {tProfile('timezone')}
            </label>
          </Flex>
          <select
            id="business_utc"
            name="business_utc"
            value={settings.business_utc}
            onChange={onChange}
            className={inputBaseClass}
          >
            {timeZones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {/* Валюта */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <CreditCard className={`w-4 h-4 text-primary`} />
            <label htmlFor="business_currency" className={labelClass}>
              {tProfile('business_currency')}
            </label>
          </Flex>
          <select
            id="business_currency"
            name="business_currency"
            value={settings.business_currency}
            onChange={onChange}
            className={inputBaseClass}
          >
            {currencies.map(currency => (
              <option key={currency.value} value={currency.value}>
                {currency.label}
              </option>
            ))}
          </select>
        </div>

        {/* Аналитические параметры */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <TrendingUp className={`w-4 h-4 text-primary`} />
            <label className={labelClass}>
              {tProfile('analytics_parameters')}
            </label>
          </Flex>
          
          {/* Маржа */}
          <div className="mt-2">
            <label htmlFor="margin" className={`${labelClass} text-sm`}>
              {tProfile('margin')} (%)
            </label>
            <input
              type="number"
              id="margin"
              name="margin"
              value={Math.round(settings.margin * 100)}
              onChange={(e) => handlePercentageInput(e, 'margin')}
              min="0"
              max="100"
              step="1"
              className={inputBaseClass}
            />
            <p className={`mt-1 text-xs text-[${COLORS.secondary}]`}>
              {tProfile('margin_description')}
            </p>
          </div>
          
          {/* Ставка дисконтирования */}
          <div className="mt-2">
            <label htmlFor="discount_rate" className={`${labelClass} text-sm`}>
              {tProfile('discount_rate')} (%)
            </label>
            <input
              type="number"
              id="discount_rate"
              name="discount_rate"
              value={Math.round(settings.discount_rate * 100)}
              onChange={(e) => handlePercentageInput(e, 'discount_rate')}
              min="0"
              max="100"
              step="0.5"
              className={inputBaseClass}
            />
            <p className={`mt-1 text-xs text-[${COLORS.secondary}]`}>
              {tProfile('discount_rate_description')}
            </p>
          </div>
        </div>

        {/* Инструкции для ассистента */}
        <div className={formGroupClass}>
          <Flex align="center" gap="xs">
            <Settings className={`w-4 h-4 text-primary`} />
            <label htmlFor="assistant_instructions" className={labelClass}>
              {tProfile('assistant_instructions')}
            </label>
          </Flex>
          <textarea
            id="assistant_instructions"
            name="assistant_instructions"
            rows={6}
            value={settings.assistant_instructions || ''}
            onChange={onChange}
            className={inputBaseClass}
            placeholder={tProfile('assistant_instructions_placeholder')}
          />
          <Card 
            padding="md" 
            className={`mt-2 ${COLORS.status.info.bg}`}
          >
            <h4 className={`text-sm font-medium ${COLORS.status.info.text}`}>
              {tProfile('assistant_instructions_help_title')}
            </h4>
            <p className={`mt-1 text-xs ${COLORS.status.info.text}`}>
              {getAssistantInstructionsHelp(tProfile)}
            </p>
            <ul className={`mt-2 text-xs ${COLORS.status.info.text} list-disc pl-5 space-y-1`}>
              <li>{tProfile('assistant_instructions_help_item1')}</li>
              <li>{tProfile('assistant_instructions_help_item2')}</li>
              <li>{tProfile('assistant_instructions_help_item3')}</li>
              <li>{tProfile('assistant_instructions_help_item4')}</li>
            </ul>
          </Card>
        </div>

        {success && (
          <Message 
            type="success" 
            translateKey="profile:settings_saved"
          />
        )}
        
        {error && (
          <Message 
            type="error" 
            message={error}
          />
        )}

        <Flex justify="end">
          <button
            type="submit"
            disabled={saving || !hasChanges}
            className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.md} ${COMPONENT_STYLES.button.primary}
              ${(!hasChanges || saving) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {saving ? tCommon('loading') : tCommon('save')}
          </button>
        </Flex>
      </Grid>
    </form>
  );
};

export default AppSettingsForm;